import { Injectable } from '@angular/core';
import { CookiesService } from './cookies.service';
import { ApiService } from './api.service';
import { Observable } from 'rxjs/Observable';
import { AuthService } from './auth.service';
import 'rxjs/add/observable/of';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { getObjectValue } from '../utils/getObjectValue';
import { of } from 'rxjs/observable/of';
import { isAsync } from '../utils/isAsync';
import { defer } from 'rxjs/observable/defer';
import { Certificate } from '../utils/certificate';

declare function unescape(s: string): string;

/**
 * Возвращает информацию об алгоритме. Для серверных всегда юзаем 2012_256
 * */

export interface ISignatureAlgorithm {
  objectId: string;
}

export interface IOrganisation {
  name: string;
  location: string;
  state: string;
  country: string;
  ogrn: string;
  inn: string;
  type: string[];
}

export interface IUser {
  name: string;
  name2: string;
  position: string;
  surname: string;
  surname2: string;
  patronymic: string;
  patronymic2: string;
  email: string;
  etpAccess: string;
  role: null;
}

export interface ISubject {
  name: string;
  organisation: IOrganisation;
  user: IUser;
}

export interface ICheckSignResponse {
  serialNumber: string;
  subject: ISubject;
  encodingType: number;
  thumbprint: string;
  version: number;
  issuer: string;
  signatureAlgorithm: ISignatureAlgorithm;
  serial: string;
  algorithm: string;
  timeValid: string;
  timeCreate: string;
  signTime: string;
  error: string;
  textError: string;
  etpAccess?: string;
  roles?: Array<string>;
}

export class ErrorText {
  static '00010000' = 'Удостоверяющий центр, выдавший данную электронную подпись, не авторизован на ЭТП.';
  static '80090006' = 'Ошибка проверки подписи, обратитесь в службу технической поддержки.';
  static '00000008' = 'Ошибка проверки подписи, обратитесь в службу технической поддержки.';
  static '00000021' =
    'Удостоверяющий центр, выдавший данный сертификат, не поддерживается, либо истек срок действия корневого сертификата.';
  static '00000020' =
    'Удостоверяющий центр, выдавший данный сертификат, не поддерживается, либо истек срок действия корневого сертификата.';
  static '00000040' =
    'Удостоверяющий центр, выдавший данный сертификат, не поддерживается, либо истек срок действия корневого сертификата.';
  static 'e070003b' = 'Отсутствует список отозванных сертификатов для данного удостоверяющего центра.';
  static 'e070003c' = 'Истек срок действия списка отозванных сертификатов для данного удостоверяющего центра.';
  static 'e070004c' = 'Срок действия Вашего закрытого ключа истек.';
  static 'e070004d' = 'Срок начала действия сертификата еще не наступил. Повторите попытку позже.';
  static 'e0700036' = 'Срок действия сертификата истек.';
  static 'e0731337' = 'Ошибка разбора JSON.';
  static 'e0736666' = 'Ошибка запуска программы интерфейса КС.';
  static 'auth0032' =
    'Сертификат ключа подписи получен после приостановления статуса Авторизованного УЦ и не может быть использован на площадке.';
  static 'auth0211' = 'Сертификат Вашей учетной записи был зарегистрирован в УЦ после его авторизации на ЭТП. ' +
    'Сертификат, зарегистрированный в данном УЦ до его авторизации считается недействительным на ЭТП.';
  static 'auth0222' = 'Сертификат вашей учетной записи был зарегистрирован в неавторизованном УЦ после 1 октября 2010 года ' +
    'и считается недействительным на ЭТП.';
  static 'auth0223' = 'Сертификат вашей учетной записи, выданный неавторизованным УЦ, считается недействительным.';
  static 'e02000a8' = 'При обработке данных произошла ошибка использования сессии криптосервера. Обратитесь в Техподдержку.';
  static 'e02000a9' = 'При обработке данных произошла ошибка. Данная операция не разрешена на сессии криптосервера. ' +
    'Обратитесь в Техподдержку.';
  static 'notForEtp' = 'Выбранный сертификат не предназначен для работы с ЭТП';

}

@Injectable()
export class CertificatesService {

  // Константы cadesplugin
  private static readonly CAPICOM_CURRENT_USER_STORE = 2;
  private static readonly CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED = 2;
  private static readonly CAPICOM_MY_STORE = 'My';
  private static readonly CADESCOM_HASH_ALGORITHM_CP_GOST_3411 = 100;
  private static readonly CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_256 = 101;
  private static readonly CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_512 = 102;
  private static readonly CAPICOM_CERTIFICATE_FIND_SHA1_HASH = 0;
  private static readonly CADESCOM_CADES_BES = 1;
  private static readonly CADESCOM_BASE64_TO_BINARY = 1;
  private static readonly CADESCOM_STRING_TO_UCS2LE = 0;
  private static readonly CAPICOM_CERTIFICATE_INCLUDE_CHAIN_EXCEPT_ROOT = 0;
  private static readonly CAPICOM_CERTIFICATE_INCLUDE_WHOLE_CHAIN = 1;
  private static readonly CAPICOM_CERTIFICATE_INCLUDE_END_ENTITY_ONLY = 2;

  private static readonly AUTHENTICATED_ATTRIBUTE_SIGNING_TIME = 0;


  private certsList = [];
  private certificateSN;
  private issuerDN;

  constructor(
    private cookies: CookiesService,
    private api: ApiService,
    private service: AuthService,
    private http: HttpClient,
  ) {
  }

  private _window(): any {
    return window;
  }

  /**
   * Проверка подписи
   * @param id // документа
   */
  public checkSign(id): Observable<ICheckSignResponse | any> {
    const url = 'document/check-sign';
    return this.api.post(url, {document_id: id})
      .pipe(
        map(res => {
          if (res['data'] !== undefined) {
            return <ICheckSignResponse>res['data'];
          } else {
            return {};
          }
        })
      );
  }

  /**
   * Получение списка сертификатов
   * @param resetCache - необходимо ли обновить закешированный список сертификатов
   */
  public getCertsList(resetCache = false): Observable<never | Array<Certificate>> {
    if (!resetCache && this.certsList.length > 0) {
      return of(this.certsList);
    }

    if (this.cookies.get('useCryptoServer') === 'true') {
      return this._getCertListFromCrypto();
    }

    if (isAsync()) {
      return this._getCertsListAsync();
    } else {
      return this._getCertsListSync();
    }
  }

  public setCertsList(certificateSN: string, issuerDN: string) {
    this.certificateSN = certificateSN;
    this.issuerDN = issuerDN;
  }

  /**
   * Асинхронное получение списка сертификатов
   * @private
   */
  protected _getCertsListAsync(): Observable<never | Array<Certificate>> {
    return defer(async () => {
      await window.cadesplugin;
      const cadesplugin = window.cadesplugin;

      const oStore = await cadesplugin.CreateObjectAsync('CAdESCOM.Store');

      // Открываем хранилище
      try {
        await oStore.Open(
          await cadesplugin.CAPICOM_CURRENT_USER_STORE,
          await cadesplugin.CAPICOM_MY_STORE,
          await cadesplugin.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED,
        );
      } catch (err) {
        throw new Error('Ошибка при открытии хранилища: ' + err.message);
      }

      let count = 0;
      let certs = null;
      const result = [];
      // Получаем доступ к сертификатам
      try {
        certs = await oStore.Certificates;
        if (certs) {
          certs = await certs.Find(await cadesplugin.CAPICOM_CERTIFICATE_FIND_TIME_VALID);
          /**
           * Не рассматриваются сертификаты, в которых отсутствует закрытый ключ
           * или не действительны на данный момент
           * */
          certs = await certs.Find(
            await cadesplugin.CAPICOM_CERTIFICATE_FIND_EXTENDED_PROPERTY,
            await cadesplugin.CAPICOM_PROPID_KEY_PROV_INFO,
          );

          count = await certs.Count;
        }
      } catch (err) {
        throw new Error('Ошибка получения списка сертификатов: ' + err.message);
      }
      //
      if (!count) {
        throw new Error('Нет доступных сертификатов');
      }

      try {
        while (count) {
          const item = await certs.Item(count);
          result.push(
            new Certificate({
              _cert: await item,
              thumbprint: await item.Thumbprint,
              subjectName: await item.SubjectName,
              issuerName: await item.IssuerName,
              validFrom: await item.ValidFromDate,
              validTo: await item.ValidToDate,
              serialNumber: await item.SerialNumber,
              provider: defer(async () => {
                let provider = null;
                if (await item.HasPrivateKey()) {
                  const privateKey = await item.PrivateKey;
                  provider = await privateKey.ProviderName;
                }
                return provider;
              }),
              privateKeyLink: defer(async () => {
                let privateKeyLink = null;
                const hasPrivateKey = await item.HasPrivateKey();
                if (hasPrivateKey) {
                  const privateKey = await item.PrivateKey;
                  try {
                    privateKeyLink = await privateKey.UniqueContainerName;
                  } catch (e) {
                    privateKeyLink = 'Набор ключей не существует';
                  }
                }
                return privateKeyLink;
              }),
              algorithm: defer(async () => {
                const publicKey = await item.PublicKey();
                const algorithm = await publicKey.Algorithm;
                return await algorithm.FriendlyName;
              }),
              isStore: true,
              isValid: defer(async () => {
                const isValid = await item.IsValid();
                return await isValid.Result;
              }),
            }),
          );

          count--;
        }
      } catch (err) {
        throw new Error('Ошибка обработки сертификатов: ' + err.message);
      }

      await oStore.Close();

      this.certsList = this.prepareCertsInfo(result);
      return this.certsList;
    });
  }

  /**
   * Синхронное получение списка сертификатов
   * @private
   */
  protected _getCertsListSync(): Observable<never | Array<Certificate>> {
    const cadesplugin = window.cadesplugin;
    const oStore = cadesplugin.CreateObject('CAdESCOM.Store');
    // Открываем хранилище
    try {
      oStore.Open(cadesplugin.CAPICOM_CURRENT_USER_STORE, cadesplugin.CAPICOM_MY_STORE, cadesplugin.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED);
    } catch (err) {
      return Observable.throw('Ошибка при открытии хранилища: ' + err.message);
    }

    let count = 0;
    let certs = null;
    const result = [];
    // Получаем доступ к сертификатам
    try {
      certs = oStore.Certificates;
      if (certs) {
        certs = certs.Find(cadesplugin.CAPICOM_CERTIFICATE_FIND_TIME_VALID);
        /**
         * Не рассматриваются сертификаты, в которых отсутствует закрытый ключ
         * или не действительны на данный момент
         * */
        certs = certs.Find(cadesplugin.CAPICOM_CERTIFICATE_FIND_EXTENDED_PROPERTY, cadesplugin.CAPICOM_PROPID_KEY_PROV_INFO);

        count = certs.Count;
      }
    } catch (err) {
      return Observable.throw('Ошибка получения списка сертификатов: ' + err.message);
    }

    if (!count) {
      return Observable.throw('Нет доступных сертификатов');
    }

    try {
      while (count) {
        const item = certs.Item(count);
        result.push(
          new Certificate({
            _cert: item,
            thumbprint: item.Thumbprint,
            subjectName: item.SubjectName,
            issuerName: item.IssuerName,
            validFrom: item.ValidFromDate,
            validTo: item.ValidToDate,
            serialNumber: item.SerialNumber,
            provider: of(() => {
              let provider = null;
              if (item.HasPrivateKey()) {
                provider = item.PrivateKey.ProviderName;
              }
              return provider;
            }),
            privateKeyLink: of(() => {
              let privateKeyLink = null;
              const hasPrivateKey = item.HasPrivateKey();
              if (hasPrivateKey) {
                const privateKey = item.PrivateKey;
                try {
                  privateKeyLink = privateKey.UniqueContainerName;
                } catch (e) {
                  privateKeyLink = 'Набор ключей не существует';
                }
              }
              return privateKeyLink;
            }),
            algorithm: of(() => {
              const publicKey = item.PublicKey();
              const algorithm = publicKey.Algorithm;
              return algorithm.FriendlyName;
            }),
            isStore: true,
            isValid: of(() => {
              const isValid = item.IsValid();
              return isValid.Result;
            }),
          }),
        );

        count--;
      }
    } catch (err) {
      return Observable.throw('Ошибка обработки сертификатов: ' + err.message);
    }

    oStore.Close();

    this.certsList = this.prepareCertsInfo(result);

    return of(this.certsList);
  }

  /**
   * Возвращает дату в формате (dd.mm.yyyy hh:mm:ss) из строки, формата, используемого плагином cryptoPro
   * */
  getReadableDate(date: string): string {
    const dateObj = new Date(date);

    return ([
      dateObj.getDate(),
      dateObj.getMonth() + 1,
      dateObj.getFullYear()
    ].join('.') + ' ' + [
      dateObj.getHours(),
      dateObj.getMinutes(),
      dateObj.getSeconds()
    ].join(':')).replace(/\b(\d)\b/g, '0$1');
  }

  /**
   * Подготавливает информацию о сертификатах
   * @param items - массив сертификатов
   */
  protected prepareCertsInfo(items: Array<Certificate>): Array<Certificate> {
    return items.map((c) => {
      const matchName = c.subjectName.match(/CN=(.+?)(?:,|$)/);

      // Удалось ли вытащить Common Name
      if (matchName && matchName[1]) {
        c.name = matchName[1];
      }

      c.validFrom = this.getReadableDate(c.validFrom);
      c.validTo = this.getReadableDate(c.validTo);

      c.label = c.name + ' (до ' + c.validTo + ')';

      return c;
    });
  }

  /**
   * Получение списка устанвленных сертиификатов из серверного хранилища
   */
  protected _getCertListFromCrypto(): Observable<never | Array<Certificate>> {
    return this.http.post('/crypto/api', '{"method":"Server.getCertificates","id":1,"params":{}}')
      .pipe(
        map(res => {
          const result = getObjectValue(res, 'result');
          if (result) {
            const ret = [];
            for (const certAlias in result) {
              if (result.hasOwnProperty(certAlias)) {
                const item: any = result[certAlias];
                ret.push(new Certificate({
                  _cert: null,
                  thumbprint: certAlias,
                  subjectName: item.subject,
                  issuerName: item.issuer,
                  validFrom: item.notBefore * 1000,
                  validTo: item.notAfter * 1000,
                  serialNumber: item.serialNumber,
                }));
              }
            }
            this.certsList = this.prepareCertsInfo(ret);
            return this.certsList;
          } else {
            return [];
          }
        })
      );
  }

  private getCertObject(thumbprint: string) {
    for (const cert of this.certsList) {
      if (cert.thumbprint === thumbprint) {
        return cert;
      }
    }
  }

  private getAlgorithmCodeAsync(certificate) {
    return new Promise(function (resolve, reject) {
      certificate.getAlgorithm().then(
        algorithm => {
          switch (algorithm.oid) {
            case '1.2.643.2.2.19':
              resolve(CertificatesService.CADESCOM_HASH_ALGORITHM_CP_GOST_3411);
              break;
            case '1.2.643.7.1.1.1.1':
              resolve(CertificatesService.CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_256);
              break;
            case '1.2.643.7.1.1.1.2':
              resolve(CertificatesService.CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_512);
              break;
          }

          reject('Алгоритм подписи не поддерживается: ' + algorithm.algorithm);
        },
        algorithm_error => {
          reject(algorithm_error);
        }
      );
    });
  }


  private filterLocalCertificates(certs: any[]) {
    if (environment.only_user_cert) {
      this.service.get_user_cert().subscribe(
        data => {
          this.setCertsList(data['certificateSN'], data['issuerDN']);

          const certificateSN = this.certificateSN;
          const issuerDN = this.issuerDN;

          if (isAsync()) {
            const promises = [];
            for (const cert of certs) {
              promises.push(new Promise(resolve => {
                return cert._cert.SerialNumber.then(
                  res => {
                    if (
                      certificateSN.toLowerCase() === res.toLowerCase()
                      /* && this._check_issuer_identity(issuerDN.toLowerCase(), cert.issuerName.toLowerCase()) */
                    ) {
                      this.certsList.push(cert);
                    }
                  }
                );
              }));
            }

            Promise.all(promises);
          } else {
            for (const cert of certs) {
              let SerialNumber = cert._cert.SerialNumber;
              SerialNumber = SerialNumber.toLowerCase();

              let IssuerName = cert._cert.issuerName;
              IssuerName = IssuerName.toLowerCase();

              if (
                certificateSN.toLowerCase() === SerialNumber
                /* && this._check_issuer_identity(issuerDN.toLowerCase(), IssuerName) */
              ) {
                this.certsList.push(cert);
              }
            }
          }
        },
        error => {
          console.log(error.message);
        });
    } else {
      for (const cert of certs) {
        this.certsList.push(cert);
      }
    }
  }

  /**
   * Метод проверки идентичности issuer сертификатов
   * @param issuer_1
   * @param issuer_2
   * @private
   */
  private _check_issuer_identity(issuer_1: string, issuer_2: string): boolean {
    const prepared_issuer_1 = this._prepare_for_identity_check(issuer_1);
    const prepared_issuer_2 = this._prepare_for_identity_check(issuer_2);

    for (let i = 0; i < prepared_issuer_1.length; i++) {
      if (prepared_issuer_2.indexOf(prepared_issuer_1[i]) < 0) {
        return false;
      }
    }

    return true;
  }

  /**
   * Подготовить issuer к проверке
   * @param issuer
   * @private
   */
  private _prepare_for_identity_check(issuer: string): string[] {
    const prepared_data = [];

    issuer = issuer.replace(/'/gi, '');
    issuer = issuer.replace(/"/gi, '');

    const issuer_array = issuer.split(',');
    for (let i = 0; i < issuer_array.length; i++) {
      prepared_data.push(issuer_array[i].replace(/[a-zA-Z\s]+=/gi, ''));
    }

    return prepared_data;
  }

  /**
   * Подписать переданный гостовый хэш по определённому алгоритму
   * @param thumbprint - отпечаток сертификата
   * @param hash_content - хэш-контент
   */
  public signHash(
    thumbprint: string,
    hash_content: string | string[],
  ): Promise<any> {
    const cert = this.getCertObject(thumbprint);

    if (this.cookies.get('useCryptoServer') === 'true') {
      const myhash = environment.gost_check ? hash_content[1] : hash_content[0];
      console.log('signHash server');
      return this._signByCrypto(btoa(myhash), thumbprint).toPromise();
    }

    return new Promise((resolve, reject) => {
      if (isAsync()) {
        this.getAlgorithmCodeAsync(cert).then(
          algorithm => {
            this._signHash_asAsync(thumbprint, hash_content, parseInt(algorithm.toString(), 10)).then(
              signature => {
                resolve(signature);
              },
              error_msg => {
                reject(error_msg);
              }
            );
          }
        );
      } else {
        this.getAlgorithmCodeAsync(cert).then(
          algorithm => {
            const result = this._signHash_asSync(thumbprint, hash_content, parseInt(algorithm.toString(), 10));
            if (result['signature']) {
              resolve(result);
            } else {
              reject(result['error_msg']);
            }
          }
        );
      }
    });
  }


  public getGostHash(content: string, thumbprint, base64input: boolean = false): Promise<any> {
    const self = this;
    const certificate = this.getCertObject(thumbprint);

    if (!base64input) {
      content = this.base64encode(content);
    }

    if (this.cookies.get('useCryptoServer') === 'true') {
      console.log('signHash server');
      return this._hashByCrypto(content).toPromise();
    }

    if (isAsync()) {
      return new Promise((resolve, reject) => {
        this.getAlgorithmCodeAsync(certificate).then(
          algorithm => {
            this._window().cadesplugin.async_spawn(function* (arg) {
              try {
                const oHashedData = yield self._window().cadesplugin.CreateObjectAsync('CAdESCOM.HashedData');
                yield oHashedData.propset_Algorithm(environment.gost_check
                  ? algorithm
                  : CertificatesService.CADESCOM_HASH_ALGORITHM_CP_GOST_3411);
                yield oHashedData.propset_DataEncoding(0x01);

                oHashedData.Hash(content);
                const hash = yield oHashedData.Value;


                resolve(hash);

              } catch (error) {
                reject(error.message);
              }
            }, resolve, reject);
          },
          algoerror => {
            reject(algoerror);
          }
        );


      });
    } else {
      return new Promise((resolve, reject) => {
        this.getAlgorithmCodeAsync(certificate).then(
          algorithm => {
            const oHashedData = self._window().cadesplugin.CreateObject('CAdESCOM.HashedData');
            oHashedData.Algorithm = environment.gost_check ? algorithm : CertificatesService.CADESCOM_HASH_ALGORITHM_CP_GOST_3411;
            oHashedData.DataEncoding = 0x01;

            oHashedData.Hash(content);

            resolve(oHashedData.Value);
          },
          algoerror => {
            reject(algoerror);
          }
        );
      });
    }
  }

  /**
   * Подпись данных на серверной крипте
   * @param xml - данные для подписи
   * @param thumbprint - отпечаток сертификата
   */
  protected _signByCrypto(xml: string, thumbprint: string): Observable<never | Object> {
    // tslint:disable-next-line:max-line-length
    return this.http.post('/crypto/api', '{"method":"Signature.signMessage","id":1,"params":{"alias":' + thumbprint + ',"password":"","message":"' + xml + '"}}')
      .pipe(
        map(res => {
          const result = getObjectValue(res, 'result');
          console.log(result);
          return {
            signature: result,
            hash: xml
          };
        })
      );
  }

  /**
   * ХЕШ данных на серверной крипте
   * @param xml - данные для подписи
   * @param thumbprint - отпечаток сертификата
   */
  protected _hashByCrypto(base64string: string): Observable<never | Object> {
    return this.http.post('/crypto/api', '{"method":"Signature.hashData","id":1,"params":{"content":"' + base64string + '"}}')
      .pipe(
        map(res => {
          const result = getObjectValue(res, 'result');
          console.log('server hash=' + result);
          return result;
        })
      );
  }

  /**
   * Подписать хэш контент асинхронно (например с помощью интерпретатора браузера Chrome)
   * @param thumbprint
   * @param hash_content
   * @param algorithm
   * @param signType - тип подписи открепленная (true) / присоединенная (false)
   * @protected
   */
  protected _signHash_asAsync(thumbprint: string, hash_content: string | string[], algorithm: number, signType = true): Promise<any> {
    const self = this;

    let hash = hash_content;
    if (Array.isArray(hash)) {
      switch (algorithm) {
        case CertificatesService.CADESCOM_HASH_ALGORITHM_CP_GOST_3411:
          hash = hash_content[0];
          break;
        case CertificatesService.CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_256:
          hash = environment.gost_check ? hash_content[1] : hash_content[0];
          break;
        case CertificatesService.CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_512:
          hash = hash_content[2];
          break;
      }
    }
    console.log('Попытка подписать hash:', hash);
    return new Promise((resolve, reject) => {
      this._window().cadesplugin.async_spawn(function* (arg) {
        try {
          const oStore = yield self._window().cadesplugin.CreateObjectAsync('CAdESCOM.Store');
          yield oStore.Open(
            CertificatesService.CAPICOM_CURRENT_USER_STORE,
            CertificatesService.CAPICOM_MY_STORE,
            CertificatesService.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED
          );
          const all_certs = yield oStore.Certificates;
          const oCerts = yield all_certs.Find(CertificatesService.CAPICOM_CERTIFICATE_FIND_SHA1_HASH, thumbprint);
          const oCertificate = yield oCerts.Item(1);

          const oSigner = yield self._window().cadesplugin.CreateObjectAsync('CAdESCOM.CPSigner');
          yield oSigner.propset_Certificate(oCertificate);
          const attrs = yield oSigner.AuthenticatedAttributes2;
          const oAttrs = yield self._window().cadesplugin.CreateObjectAsync('CADESCOM.CPAttribute');

          const clientTime = new Date();
          yield oAttrs.propset_Name(CertificatesService.AUTHENTICATED_ATTRIBUTE_SIGNING_TIME);
          yield oAttrs.propset_Value(clientTime);

          yield attrs.Add(oAttrs);
          const oSignedData = yield self._window().cadesplugin.CreateObjectAsync('CAdESCOM.CadesSignedData');
          yield oSignedData.propset_ContentEncoding(CertificatesService.CADESCOM_BASE64_TO_BINARY);
          yield oSignedData.propset_Content(hash);
          yield oSigner.propset_Options(CertificatesService.CAPICOM_CERTIFICATE_INCLUDE_END_ENTITY_ONLY);

          // yield oSignedData.VerifyHash(oHashedData, oSigner, CertificatesService.CADESCOM_CADES_BES);
          const signature = yield oSignedData.SignCades(
            oSigner,
            CertificatesService.CADESCOM_CADES_BES,
            signType
          );

          resolve({
            signature: signature,
            hash: hash,
          });
        } catch (error) {
          console.log('Не смог ', hash, error);
          reject(error.message);
        }
      }, resolve, reject);
    });
  }

  /**
   * Подписать хэш контент синхронно (например с помощью интерпретатора браузера Explorer)
   * @param thumbprint
   * @param hash_content
   * @param algorithm
   * @param signType - тип подписи открепленная (true) / присоединенная (false)
   * @protected
   */
  protected _signHash_asSync(thumbprint: string, hash_content: string | string[], algorithm: number, signType = true): object {
    const result = {signature: null, error_msg: null, hash: null};

    console.log('СИНХРОННЫЙ ХЕШ КАК МНОГО В ЭТОМ ЗВУКЕ');

    let hash = hash_content;
    if (Array.isArray(hash)) {
      switch (algorithm) {
        case CertificatesService.CADESCOM_HASH_ALGORITHM_CP_GOST_3411:
          hash = hash_content[0];
          break;
        case CertificatesService.CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_256:
          hash = environment.gost_check ? hash_content[1] : hash_content[0];
          break;
        case CertificatesService.CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_512:
          hash = hash_content[2];
          break;
      }
    }
    result.hash = hash;

    try {
      const oStore = this._window().cadesplugin.CreateObject('CAdESCOM.Store');
      oStore.Open(
        CertificatesService.CAPICOM_CURRENT_USER_STORE,
        CertificatesService.CAPICOM_MY_STORE,
        CertificatesService.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED
      );

      const all_certs = oStore.Certificates;
      const oCerts = all_certs.Find(CertificatesService.CAPICOM_CERTIFICATE_FIND_SHA1_HASH, thumbprint);
      const oCertificate = oCerts.Item(1);

      // let oHashedData = this._window().cadesplugin.CreateObject('CAdESCOM.HashedData');
      // oHashedData.Algorithm = algorithm;
      // oHashedData.SetHashValue(hash);

      const oSigner = this._window().cadesplugin.CreateObject('CAdESCOM.CPSigner');

      oSigner.Certificate = oCertificate;

      const attrs = oSigner.AuthenticatedAttributes2;
      let clientTime = new Date();
      if ('getVarDate' in clientTime) {
        clientTime = (clientTime as any).getVarDate();
      }

      const oAttrs = this._window().cadesplugin.CreateObject('CADESCOM.CPAttribute');
      oAttrs.Name = CertificatesService.AUTHENTICATED_ATTRIBUTE_SIGNING_TIME;
      oAttrs.Value = clientTime;
      attrs.Add(oAttrs);

      const oSignedData = this._window().cadesplugin.CreateObject('CAdESCOM.CadesSignedData');
      oSignedData.ContentEncoding = CertificatesService.CADESCOM_BASE64_TO_BINARY;
      oSignedData.Content = hash;

      oSigner.Options = CertificatesService.CAPICOM_CERTIFICATE_INCLUDE_END_ENTITY_ONLY;

      result.signature = oSignedData.SignCades(
        oSigner,
        CertificatesService.CADESCOM_CADES_BES,
        signType
      );
      return result;
    } catch (error) {
      console.log('Не смог ', hash);
      result.error_msg = error.message;
      return result;
    }
  }

  /**
   * Конвертация строки в base64
   * @param content
   */
  public base64encode(content: string): string {
    return btoa(unescape(encodeURIComponent(content)));
  }
}



// WEBPACK FOOTER //
// ./src/app/services/certificates.service.ts